import { Injectable } from "@angular/core";
import { doc, Firestore, onSnapshot } from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthStateService } from "@core/auth-state.service";
import { CatchErrorService } from "@core/catch-error.service";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { CreateUser } from "@interfaces/create-user.i";
import { BehaviorSubject, firstValueFrom, map, Observable, of, shareReplay, switchMap, take } from "rxjs";
import { CommonVariable } from "src/app/app.constants";
import { FirestoreDataService } from "src/app/services/firestore-data.service";
import { UserManagementService } from "src/app/services/user-management.service";

@Injectable({
  providedIn: "root",
})
export class UserfirebaseService {
  userRequestForHome$ = new BehaviorSubject<Data>(null);
  appUpdateOnLoginTrigger$ = new BehaviorSubject(false);

  userId = this.authService.getUid$("UserFirebaseService").pipe(
    switchMap(userId => {
      if (!userId) {
        return of(null);
      }
      return userId;
    })
  );
  secretKey: string;

  constructor(
    private firestore: Firestore,
    private catchService: CatchErrorService,
    private userManagaementService: UserManagementService,
    private firestoredataService: FirestoreDataService,
    private authService: AuthService,
    private authStateService: AuthStateService
  ) {
    this.userId = this.authService.getUid$("ChatbotWidgetService").pipe(
      switchMap((userId) => {
        return of(userId) || of(null);
      })
    );
  }

  createFbUser(user: CreateUser, createUserResponse, existingUser?: boolean) {
    // Keep parity with AuthService.createFbUser: social flows need a spoof MSN.
    let phoneNumber =
      user?.phoneNumber || createUserResponse?.spoof_generated_msn || null;
    if (!phoneNumber && user?.social_id && user?.connect_type) {
      try {
        phoneNumber = localStorage.getItem("anonymousId");
      } catch {
        phoneNumber = null;
      }
    }

    const request = {
      customer_id: createUserResponse?.customer_id,
      user_id: createUserResponse?.user_id,
      password: user?.password,
      phoneNumber,
      firstName: user?.firstName,
      lastName: user?.lastName,
      user_exists: existingUser ?? false,
    };

    const data = {
      userDetails: request,
    };
    return this.firestoredataService.getV2CloudFunction("createfirebaseuserv2", data);
  }

  public async updateUserFingerprintRequest(appVersionId) {
    return this.authService.getUid$("UserFirebaseService").pipe(
      switchMap(async (userId) => {
        if (!userId) return null;
        const ipInfo = await firstValueFrom(this.userManagaementService.getUserCountryBaseOnIP());
        const fingerPrint = await this.fingerprintJS();
        const payload = {
          userId: userId,
          fingerprintId: fingerPrint,
          userAgent: navigator?.userAgent,
          appVersion: appVersionId ?? null,
          userIP: ipInfo?.ip ?? null,
        };
        this.firestoredataService.getV2CloudFunction("updateuserfingerprintv2", payload).subscribe();
        return of(userId) || of(null);
      }));

  }

  async fingerprintJS() {
    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      const visitorId = result.visitorId;
      return visitorId;
    } catch (error) {
      console.error('Error generating fingerprint:', error);
      return null; // Return null instead of the error object
    }
  }

  checkAppUpdateOnLogin() {
    this.userRequestForHome$?.subscribe({
      next: (user) => {
        if (!user) {
          return;
        }
        const userId = user?.uid;
        const fingerprintId = CommonVariable.FingerprintJSConst
          ? CommonVariable.FingerprintJSConst
          : localStorage.getItem("fingerSalt");
        if (user && user?.uid) {
          const versionCheckPayload = {
            userId,
            fingerprintId,
          };
          this.firestoredataService.getV2CloudFunction("appversioncheckv2", versionCheckPayload)?.subscribe({
            next: () => {
              this.getUserPwaVersionData(userId)?.subscribe({
                next: (pwaVersionData) => {
                  if (pwaVersionData?.sw_update_required) {
                    this.appUpdateOnLoginTrigger$.next(true);
                  }
                },
                error: (error) => {
                  return error;
                },
              });
            },
            error: (err) => {
              return err;
            },
          });
        }
      },
      error: (err) => {
        return err;
      },
    });
  }

  public getUserPwaVersionData(userId: string) {
    // Gate on customTokenReady$ to prevent permission-denied errors on iOS/Safari
    // where the Firebase custom token may not yet be applied when this listener starts.
    return this.authStateService.customTokenReady$.pipe(
      take(1),
      switchMap(() =>
        this.catchService.isOnline$.pipe(
          switchMap((isOnline) => {
            if (!isOnline) {
              return of(null); // Handle offline scenario gracefully
            }

            const userDocRef = doc(this.firestore, `users/${userId}`);
            return new Observable((subscriber) => {
              const unsubscribe = onSnapshot(
                userDocRef,
                (snapshot) => subscriber.next(snapshot),
                (error) => subscriber.error(error)
              );
              return () => unsubscribe();
            }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          shareReplay(1),
          map((data: any) => data?.data()) // Extract the document data from the snapshot
        )
      )
    );
  }
  // update user grade based on sms verification status
  updateUserGrade(grade: string) {
    try {
      this.authService.getUid$("UserFirebaseService").pipe(
        switchMap((userId) => {
          const payload = {
            user_id: userId,
            user_grade: grade,
          };
          return this.firestoredataService.getV2CloudFunction("updateusergradev2", payload);
        })
      ).subscribe(); // <-- This is required!
    } catch (error) {
      return error;
    }
  }

  // // This function is used to update the user's leaderboard

  updateUserLeaderBoard() {
    const updateUserLeaderBoard = this.authService.getV2HttpsCallableFunction("peepsoleaderboard");
    updateUserLeaderBoard().catch((error) => {
      throw new Error(`Error in updateUserLotteryDiscovery: ${error.message}`);
    });
  }

  updateUserLotteryDiscovery() {
    const updateUserLotteryDiscovery = this.authService.getV2HttpsCallableFunction("updateuserlotterydiscoveryv2");
    updateUserLotteryDiscovery().catch((error) => {
      throw new Error(`Error in updateUserLotteryDiscovery: ${error.message}`);
    });
  }
}
